import type { Direction } from "./direction";
import { directionDelta } from "./direction";

export class XY {
	readonly x: number;
	readonly y: number;

	constructor(x: number, y: number) {
		this.x = x;
		this.y = y;
	}

	equals(other: XY): boolean {
		return this.x === other.x && this.y === other.y;
	}

	add(other: XY): XY;
	add(dx: number, dy: number): XY;
	add(dxOrOther: XY | number, dy?: number): XY {
		if (typeof dxOrOther === "number") {
			return new XY(this.x + dxOrOther, this.y + dy!);
		}
		return new XY(this.x + dxOrOther.x, this.y + dxOrOther.y);
	}

	addDirection(dir: Direction): XY {
		return this.add(directionDelta(dir));
	}

	subtract(other: XY): XY {
		return new XY(this.x - other.x, this.y - other.y);
	}

	distanceTo(other: XY): number {
		const dx = this.x - other.x;
		const dy = this.y - other.y;
		return Math.sqrt(dx * dx + dy * dy);
	}

	distanceSquared(other: XY): number {
		const dx = this.x - other.x;
		const dy = this.y - other.y;
		return dx * dx + dy * dy;
	}

	isInSquareRadius(other: XY, radius: number): boolean {
		return Math.abs(this.x - other.x) <= radius && Math.abs(this.y - other.y) <= radius;
	}

	isAlignedWith(other: XY): boolean {
		const dx = this.x - other.x;
		const dy = this.y - other.y;
		return dx === 0 || dy === 0 || Math.abs(dx) === Math.abs(dy);
	}

	getNeighbors(): XY[] {
		return [
			new XY(this.x, this.y - 1),
			new XY(this.x + 1, this.y - 1),
			new XY(this.x + 1, this.y),
			new XY(this.x + 1, this.y + 1),
			new XY(this.x, this.y + 1),
			new XY(this.x - 1, this.y + 1),
			new XY(this.x - 1, this.y),
			new XY(this.x - 1, this.y - 1),
		];
	}

	getCardinalNeighbors(): XY[] {
		return [
			new XY(this.x, this.y - 1),
			new XY(this.x + 1, this.y),
			new XY(this.x, this.y + 1),
			new XY(this.x - 1, this.y),
		];
	}

	getDiagonalNeighbors(): XY[] {
		return [
			new XY(this.x + 1, this.y - 1),
			new XY(this.x + 1, this.y + 1),
			new XY(this.x - 1, this.y + 1),
			new XY(this.x - 1, this.y - 1),
		];
	}

	getAreaInSquareRadius(radius: number): XY[] {
		const result: XY[] = [];
		for (let dx = -radius; dx <= radius; dx++) {
			for (let dy = -radius; dy <= radius; dy++) {
				result.push(new XY(this.x + dx, this.y + dy));
			}
		}
		return result;
	}

	getAreaTo(other: XY): XY[] {
		const minX = Math.min(this.x, other.x);
		const maxX = Math.max(this.x, other.x);
		const minY = Math.min(this.y, other.y);
		const maxY = Math.max(this.y, other.y);
		const result: XY[] = [];
		for (let x = minX; x <= maxX; x++) {
			for (let y = minY; y <= maxY; y++) {
				result.push(new XY(x, y));
			}
		}
		return result;
	}

	isInside(board: { width: number; height: number }): boolean {
		return this.x >= 0 && this.x < board.width && this.y >= 0 && this.y < board.height;
	}

	isInBorder(board: { width: number; height: number }): boolean {
		return this.x === 0 || this.x === board.width - 1 || this.y === 0 || this.y === board.height - 1;
	}

	static fromString(str: string): XY {
		const stripped = str.replace(/[()]/g, "");
		const parts = stripped.split(",");
		return new XY(parseInt(parts[0]), parseInt(parts[1]));
	}

	toString(): string {
		return `(${this.x},${this.y})`;
	}
}
